"use client";

import React from "react";
import satori from "satori";
import type { Language } from "@/hooks/useLanguage";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import {
	buildPosterBackgroundUrl,
	DESKTOP_POSTER_HEIGHT,
	DESKTOP_POSTER_WIDTH,
	getPosterContent,
	getPosterDimensions,
	getPosterLayout,
	getPosterPalette,
	MOBILE_POSTER_HEIGHT,
	MOBILE_POSTER_WIDTH,
	type PosterVariant,
} from "./poster-layout";
import type { SponsoredHolding } from "./types";
import {
	buildTokenImageUrl,
	svgToDataUri,
} from "./utils";

const uiLog = createLogAdapter(
	"app/plaza/components/sponsored-ranking-banner/poster.tsx",
);

const POSTER_CACHE_NAME = "plaza-sponsored-ranking-posters";
const POSTER_CACHE_VERSION = "v8";
const POSTER_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const NORMALIZED_IMAGE_SIZE = 512;

interface PosterOptions {
	holding: SponsoredHolding;
	rank: number;
	theme: "light" | "dark";
	variant: PosterVariant;
	language: Language;
}

let fontPromise:
	| Promise<
			{
				name: string;
				data: ArrayBuffer;
				weight: 400 | 700;
				style: "normal";
			}[]
	  >
	| null = null;

const imageCache = new Map<string, Promise<string | null>>();
const posterBuildCache = new Map<string, Promise<string | null>>();

async function loadFonts() {
	if (!fontPromise) {
		fontPromise = Promise.all([
			fetch("/fonts/DejaVuSans.ttf").then(async (res) => ({
				name: "PosterSans",
				data: await res.arrayBuffer(),
				weight: 400 as const,
				style: "normal" as const,
			})),
			fetch("/fonts/DejaVuSans-Bold.ttf").then(async (res) => ({
				name: "PosterSans",
				data: await res.arrayBuffer(),
				weight: 700 as const,
				style: "normal" as const,
			})),
			fetch("/fonts/DejaVuSansMono.ttf").then(async (res) => ({
				name: "PosterMono",
				data: await res.arrayBuffer(),
				weight: 400 as const,
				style: "normal" as const,
			})),
			fetch("/fonts/DejaVuSansMono-Bold.ttf").then(async (res) => ({
				name: "PosterMono",
				data: await res.arrayBuffer(),
				weight: 700 as const,
				style: "normal" as const,
			})),
			fetch("/fonts/NotoSansSC-Regular.otf").then(async (res) => ({
				name: "PosterSansSC",
				data: await res.arrayBuffer(),
				weight: 400 as const,
				style: "normal" as const,
			})),
			fetch("/fonts/NotoSansSC-Bold.otf").then(async (res) => ({
				name: "PosterSansSC",
				data: await res.arrayBuffer(),
				weight: 700 as const,
				style: "normal" as const,
			})),
		]).catch((error) => {
			fontPromise = null;
			throw error;
		});
	}

	return fontPromise;
}

async function imageUrlToDataUri(url: string): Promise<string | null> {
	if (!imageCache.has(url)) {
		imageCache.set(
			url,
			fetch(url)
				.then(async (res) => {
					if (!res.ok) return null;
					const blob = await res.blob();
					return await normalizeImageBlobToPngDataUri(blob);
				})
				.catch(() => null),
		);
	}

	return imageCache.get(url) ?? null;
}

async function readBlobAsDataUri(blob: Blob): Promise<string> {
	return await new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(String(reader.result));
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});
}

async function loadImageElement(blob: Blob): Promise<HTMLImageElement> {
	const objectUrl = URL.createObjectURL(blob);

	try {
		const image = new Image();
		image.decoding = "async";

		await new Promise<void>((resolve, reject) => {
			image.onload = () => resolve();
			image.onerror = () => reject(new Error("Failed to decode image element"));
			image.src = objectUrl;
		});

		return image;
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}

async function decodeImageBlob(blob: Blob): Promise<CanvasImageSource> {
	if ("createImageBitmap" in window) {
		try {
			return await window.createImageBitmap(blob);
		} catch {
			// Fall through to HTMLImageElement for formats the browser decodes
			// outside the ImageBitmap path, such as some SVG or GIF variants.
		}
	}

	return await loadImageElement(blob);
}

function getSourceDimensions(source: CanvasImageSource) {
	if ("width" in source && "height" in source) {
		const width = Number(source.width);
		const height = Number(source.height);
		if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
			return { width, height };
		}
	}

	if ("videoWidth" in source && "videoHeight" in source) {
		const width = Number(source.videoWidth);
		const height = Number(source.videoHeight);
		if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
			return { width, height };
		}
	}

	return null;
}

function drawNormalizedImageToContext(
	context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
	source: CanvasImageSource,
) {
	const dimensions = getSourceDimensions(source);
	if (!dimensions) {
		throw new Error("Failed to resolve decoded image size");
	}

	const { width, height } = dimensions;
	const targetSize = NORMALIZED_IMAGE_SIZE;
	const scale = Math.min(targetSize / width, targetSize / height);
	const drawWidth = Math.max(1, Math.round(width * scale));
	const drawHeight = Math.max(1, Math.round(height * scale));
	const dx = Math.round((targetSize - drawWidth) / 2);
	const dy = Math.round((targetSize - drawHeight) / 2);

	context.clearRect(0, 0, targetSize, targetSize);
	context.imageSmoothingEnabled = true;
	context.imageSmoothingQuality = "high";
	context.drawImage(source, dx, dy, drawWidth, drawHeight);
}

async function normalizeImageBlobToPngDataUri(blob: Blob): Promise<string | null> {
	const mimeType = blob.type.toLowerCase();

	if (mimeType === "image/png") {
		return await readBlobAsDataUri(blob);
	}

	const source = await decodeImageBlob(blob);

	try {
		if ("OffscreenCanvas" in window) {
			const canvas = new OffscreenCanvas(NORMALIZED_IMAGE_SIZE, NORMALIZED_IMAGE_SIZE);
			const context = canvas.getContext("2d");
			if (!context) {
				throw new Error("Failed to create offscreen canvas context");
			}

			drawNormalizedImageToContext(context, source);
			const normalizedBlob = await canvas.convertToBlob({ type: "image/png" });
			return await readBlobAsDataUri(normalizedBlob);
		}

		const canvas = document.createElement("canvas");
		canvas.width = NORMALIZED_IMAGE_SIZE;
		canvas.height = NORMALIZED_IMAGE_SIZE;
		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("Failed to create canvas context");
		}

		drawNormalizedImageToContext(context, source);
		const normalizedBlob = await new Promise<Blob>((resolve, reject) => {
			canvas.toBlob((nextBlob) => {
				if (nextBlob) {
					resolve(nextBlob);
					return;
				}

				reject(new Error("Failed to encode normalized PNG"));
			}, "image/png");
		});

		return await readBlobAsDataUri(normalizedBlob);
	} finally {
		if ("close" in source && typeof source.close === "function") {
			source.close();
		}
	}
}

function buildPosterCacheKey({ holding, rank, variant, language }: PosterOptions) {
	return `${POSTER_CACHE_VERSION}:${variant}:${language}:r${rank + 1}:${holding.token.id}:${holding.token.customCopyZh ?? ""}:${holding.token.customCopyEn ?? ""}`;
}

function buildPosterCacheRequest(key: string) {
	return new Request(`${globalThis.location.origin}/__plaza_poster_cache__/${key}`);
}

async function openPosterCache() {
	if (typeof window === "undefined" || !("caches" in window)) {
		return null;
	}

	try {
		return await window.caches.open(POSTER_CACHE_NAME);
	} catch (error) {
		uiLog.warn("Failed to open poster cache", error);
		return null;
	}
}

async function readPosterCache(options: PosterOptions) {
	const cache = await openPosterCache();
	if (!cache) {
		return { dataUri: null, isStale: false };
	}

	try {
		const key = buildPosterCacheKey(options);
		const response = await cache.match(buildPosterCacheRequest(key));
		if (!response) {
			return { dataUri: null, isStale: false };
		}

		const dataUri = await response.text();
		const cachedAt = Number(response.headers.get("x-poster-cached-at") ?? 0);
		const isStale = !cachedAt || Date.now() - cachedAt > POSTER_CACHE_TTL_MS;

		return { dataUri: dataUri || null, isStale };
	} catch (error) {
		uiLog.warn("Failed to read cached poster", error);
		return { dataUri: null, isStale: false };
	}
}

async function writePosterCache(options: PosterOptions, dataUri: string) {
	const cache = await openPosterCache();
	if (!cache) return;

	try {
		const key = buildPosterCacheKey(options);
		const response = new Response(dataUri, {
			headers: {
				"content-type": "text/plain;charset=utf-8",
				"x-poster-cached-at": String(Date.now()),
				"x-poster-cache-version": POSTER_CACHE_VERSION,
			},
		});

		await cache.put(buildPosterCacheRequest(key), response);
	} catch (error) {
		uiLog.warn("Failed to persist poster cache", error);
	}
}

async function buildDesktopPoster(
	holding: SponsoredHolding,
	rank: number,
	theme: "light" | "dark",
	language: Language,
	fonts: {
		name: string;
		data: ArrayBuffer;
		weight: 400 | 700;
		style: "normal";
	}[],
	tokenImage: string | null,
) {
	const backgroundImage = await imageUrlToDataUri(
		buildPosterBackgroundUrl(rank, "desktop"),
	);
	const palette = getPosterPalette(rank);
	const layout = getPosterLayout("desktop");
	const content = getPosterContent(holding, "desktop", language);
	const dimensions = getPosterDimensions("desktop");
	void theme;

	return await satori(
		<div
			style={{
				display: "flex",
				position: "relative",
				width: "100%",
				height: "100%",
				overflow: "hidden",
				color: palette.textColor,
				borderRadius: layout.radius,
				backgroundColor: "#05070B",
				fontFamily: "PosterSans, PosterSansSC",
			}}
		>
			{backgroundImage ? (
				<img
					src={backgroundImage}
					alt=""
					width={DESKTOP_POSTER_WIDTH}
					height={DESKTOP_POSTER_HEIGHT}
					style={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						objectFit: "cover",
					}}
				/>
			) : null}
			<div
				style={{
					position: "absolute",
					inset: 0,
					display: "flex",
					background: layout.overlay,
				}}
			/>
			<div
				style={{
					display: "flex",
					position: "absolute",
					left: 0,
					right: 0,
					top: layout.icon.top,
					justifyContent: "center",
				}}
			>
				<div
					style={{
						display: "flex",
						width: layout.icon.containerSize,
						height: layout.icon.containerSize,
						borderRadius: 999,
						background: "rgba(5,10,20,0.22)",
						border: `${layout.icon.borderWidth}px solid ${palette.medal.colors[0]}`,
						boxShadow: `0 20px 60px ${palette.medal.glow}`,
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{tokenImage ? (
						<img
							src={tokenImage}
							alt={content.tokenName}
							width={layout.icon.size}
							height={layout.icon.size}
							style={{
								borderRadius: 999,
								objectFit: "cover",
							}}
						/>
					) : (
						<div
							style={{
								display: "flex",
								width: layout.icon.size,
								height: layout.icon.size,
								borderRadius: 999,
								alignItems: "center",
								justifyContent: "center",
								background: `linear-gradient(135deg, ${palette.medal.colors[0]} 0%, ${palette.accentSoft} 100%)`,
								color: "#FFFFFF",
								fontSize: 72,
								fontWeight: 700,
							}}
						>
							{content.monogram}
						</div>
					)}
				</div>
			</div>
			<div
				style={{
					display: "flex",
					flex: 1,
					paddingLeft: layout.content.paddingX,
					paddingRight: layout.content.paddingX,
					paddingBottom: layout.content.paddingBottom,
					flexDirection: "column",
					justifyContent: "flex-end",
				}}
			>
				<div
					style={{
						display: "flex",
						width: layout.content.panelWidth,
						maxWidth: "100%",
						padding: layout.content.panelPadding,
						flexDirection: "column",
						alignItems: "center",
						gap: layout.content.gap,
						borderRadius: layout.content.panelRadius,
						background: palette.panelBackground,
						border: `1px solid ${palette.panelBorder}`,
						boxShadow: `0 24px 70px ${palette.panelGlow}`,
						alignSelf: "center",
					}}
				>
					<div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
						<div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: layout.text.nameTickerGap,
									maxWidth: layout.text.nameMaxWidth,
									justifyContent: "center",
									flexWrap: "wrap",
								}}
							>
								<div
									style={{
										display: "flex",
										fontSize: layout.text.tickerSize,
										fontWeight: 700,
										lineHeight: 1,
										letterSpacing: -1.2,
										color: palette.textColor,
										textAlign: "center",
										justifyContent: "center",
										textShadow: "0 6px 20px rgba(0,0,0,0.28)",
									}}
								>
									{content.ticker}
								</div>
								<div
									lang={containsCjk(content.tokenName) ? "zh-CN" : undefined}
									style={{
										display: "flex",
										padding: "8px 18px",
										borderRadius: 999,
										background: "rgba(255,255,255,0.1)",
										border: `1px solid ${palette.panelBorder}`,
										fontSize: layout.text.nameSize,
										fontWeight: 600,
										lineHeight: 1,
										color: palette.secondaryText,
										textAlign: "center",
										maxWidth: layout.text.nameMaxWidth,
										fontFamily: "PosterSans, PosterSansSC",
										boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)",
									}}
								>
									{content.tokenName}
								</div>
							</div>
							<div
								lang={containsCjk(content.displayCopy) ? "zh-CN" : undefined}
								style={{
									display: "flex",
									maxWidth: layout.text.descriptionMaxWidth,
									fontWeight: 700,
									fontSize: layout.text.descriptionSize,
									lineHeight: 1.38,
									color: palette.secondaryText,
									textAlign: "center",
									justifyContent: "center",
									fontFamily: "PosterSans, PosterSansSC",
								}}
							>
								{content.displayCopy}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								fontSize: layout.text.idSize,
								fontWeight: 700,
								color: palette.textColor,
								letterSpacing: 0.4,
								textAlign: "center",
							}}
						>
							{`id: ${content.tokenId}`}
						</div>
					</div>
				</div>
			</div>
		</div>,
		{
			width: dimensions.width,
			height: dimensions.height,
			fonts,
		},
	);
}

async function buildMobilePoster(
	holding: SponsoredHolding,
	rank: number,
	theme: "light" | "dark",
	language: Language,
	fonts: {
		name: string;
		data: ArrayBuffer;
		weight: 400 | 700;
		style: "normal";
	}[],
	tokenImage: string | null,
) {
	const backgroundImage = await imageUrlToDataUri(
		buildPosterBackgroundUrl(rank, "mobile"),
	);
	const palette = getPosterPalette(rank);
	const layout = getPosterLayout("mobile");
	const content = getPosterContent(holding, "mobile", language);
	const dimensions = getPosterDimensions("mobile");
	void theme;

	return await satori(
		<div
			style={{
				display: "flex",
				position: "relative",
				width: "100%",
				height: "100%",
				overflow: "hidden",
				color: palette.textColor,
				borderRadius: layout.radius,
				backgroundColor: "#05070B",
				fontFamily: "PosterSans, PosterSansSC",
			}}
		>
			{backgroundImage ? (
				<img
					src={backgroundImage}
					alt=""
					width={MOBILE_POSTER_WIDTH}
					height={MOBILE_POSTER_HEIGHT}
					style={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						objectFit: "cover",
					}}
				/>
			) : null}
			<div
				style={{
					position: "absolute",
					inset: 0,
					display: "flex",
					background: layout.overlay,
				}}
			/>
			<div
				style={{
					display: "flex",
					position: "absolute",
					left: 0,
					right: 0,
					top: layout.icon.top,
					justifyContent: "center",
				}}
			>
				<div
					style={{
						display: "flex",
						width: layout.icon.containerSize,
						height: layout.icon.containerSize,
						borderRadius: 999,
						background: "rgba(5,10,20,0.24)",
						border: `${layout.icon.borderWidth}px solid ${palette.medal.colors[0]}`,
						boxShadow: `0 16px 44px ${palette.medal.glow}`,
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{tokenImage ? (
						<img
							src={tokenImage}
							alt={content.tokenName}
							width={layout.icon.size}
							height={layout.icon.size}
							style={{
								borderRadius: 999,
								objectFit: "cover",
							}}
						/>
					) : (
						<div
							style={{
								display: "flex",
								width: layout.icon.size,
								height: layout.icon.size,
								borderRadius: 999,
								alignItems: "center",
								justifyContent: "center",
								background: `linear-gradient(135deg, ${palette.medal.colors[0]} 0%, ${palette.accentSoft} 100%)`,
								color: "#FFFFFF",
								fontSize: 58,
								fontWeight: 700,
							}}
						>
							{content.monogram}
						</div>
					)}
				</div>
			</div>
			<div
				style={{
					display: "flex",
					flex: 1,
					paddingLeft: layout.content.paddingX,
					paddingRight: layout.content.paddingX,
					paddingBottom: layout.content.paddingBottom,
					flexDirection: "column",
					justifyContent: "flex-end",
					alignItems: "center",
				}}
			>
				<div
					style={{
						display: "flex",
						width: layout.content.panelWidth,
						maxWidth: "100%",
						padding: layout.content.panelPadding,
						flexDirection: "column",
						gap: layout.content.gap,
						borderRadius: layout.content.panelRadius,
						background: "rgba(5,10,20,0.56)",
						border: `1px solid ${palette.panelBorder}`,
						alignItems: "center",
						alignSelf: "center",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: layout.text.nameTickerGap,
							maxWidth: layout.text.nameMaxWidth,
							textAlign: "center",
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<div
							style={{
								display: "flex",
								fontSize: layout.text.tickerSize,
								fontWeight: 700,
								lineHeight: 1,
								letterSpacing: -1,
								color: palette.textColor,
								textAlign: "center",
								textShadow: "0 4px 16px rgba(0,0,0,0.24)",
							}}
						>
							{content.ticker}
						</div>
						<div
							lang={containsCjk(content.tokenName) ? "zh-CN" : undefined}
							style={{
								display: "flex",
								padding: "7px 14px",
								borderRadius: 999,
								background: "rgba(255,255,255,0.1)",
								border: `1px solid ${palette.panelBorder}`,
								fontSize: layout.text.nameSize,
								fontWeight: 600,
								lineHeight: 1,
								color: palette.secondaryText,
								textAlign: "center",
								fontFamily: "PosterSans, PosterSansSC",
							}}
						>
							{content.tokenName}
						</div>
					</div>
					<div
						lang={containsCjk(content.displayCopy) ? "zh-CN" : undefined}
						style={{
							display: "flex",
							maxWidth: layout.text.descriptionMaxWidth,
							fontSize: layout.text.descriptionSize,
							fontWeight: 700,
							lineHeight: 1.38,
							color: palette.secondaryText,
							textAlign: "center",
							fontFamily: "PosterSans, PosterSansSC",
						}}
						>
							{content.displayCopy}
						</div>
					<div
						style={{
							display: "flex",
							fontSize: layout.text.idSize,
							fontWeight: 700,
							color: palette.accentSoft,
							letterSpacing: 0.4,
							textAlign: "center",
						}}
					>
						{`id: ${content.tokenId}`}
					</div>
				</div>
			</div>
		</div>,
		{
			width: dimensions.width,
			height: dimensions.height,
			fonts,
		},
	);
}

function containsCjk(text: string) {
	return /[\u3400-\u9FFF\uF900-\uFAFF]/.test(text);
}

async function buildPosterDataUri(options: PosterOptions) {
	const { holding, rank, theme, variant, language } = options;
	const [fonts, tokenImage] = await Promise.all([
		loadFonts(),
		imageUrlToDataUri(buildTokenImageUrl(holding.token.id)),
	]);

	const svg =
		variant === "mobile"
			? await buildMobilePoster(holding, rank, theme, language, fonts, tokenImage)
			: await buildDesktopPoster(holding, rank, theme, language, fonts, tokenImage);

	return svgToDataUri(svg);
}

async function buildAndCachePosterDataUri(options: PosterOptions) {
	const cacheKey = buildPosterCacheKey(options);

	if (!posterBuildCache.has(cacheKey)) {
		posterBuildCache.set(
			cacheKey,
			(async () => {
				try {
					const dataUri = await buildPosterDataUri(options);
					await writePosterCache(options, dataUri);
					return dataUri;
				} catch (error) {
					uiLog.error("Failed to build sponsored poster", error);
					return null;
				}
			})().finally(() => {
				posterBuildCache.delete(cacheKey);
			}),
		);
	}

	return posterBuildCache.get(cacheKey) ?? null;
}

export async function getPosterDataUri(options: PosterOptions & {
	onRevalidated?: (nextDataUri: string | null) => void;
}) {
	const { onRevalidated, ...posterOptions } = options;
	const cached = await readPosterCache(posterOptions);

	if (cached.dataUri) {
		if (cached.isStale) {
			void buildAndCachePosterDataUri(posterOptions)
				.then((freshDataUri) => {
					if (freshDataUri && freshDataUri !== cached.dataUri) {
						onRevalidated?.(freshDataUri);
						return;
					}

					if (freshDataUri) {
						onRevalidated?.(freshDataUri);
					}
				})
				.catch((error) => uiLog.warn("Failed to revalidate poster cache", error));
		}

		return cached.dataUri;
	}

	return buildAndCachePosterDataUri(posterOptions);
}
